import { create } from 'zustand';
import {
  CircuitCell,
  CircuitResult,
  CnotControlCell,
  CnotTargetCell,
  runCircuit,
} from '../engine/circuitEngine';

export const MAX_STEPS = 7;
export const MAX_QUBITS = 3;

export interface PresetCircuit {
  id: string;
  name: string;
  description: string;
  numQubits: 1 | 2 | 3;
  initialStates: ('0' | '1')[];
  grid: (CircuitCell | null)[][];
  concepts: string[];
  explanation: string;
}

// ─── Preset circuits ──────────────────────────────────────────────────────────

export const PRESET_CIRCUITS: PresetCircuit[] = [
  {
    id: 'superposition',
    name: '⚛️ Superposition (H gate)',
    description: 'Apply a Hadamard gate to create an equal superposition.',
    numQubits: 1,
    initialStates: ['0'],
    grid: [
      [{ gate: 'H' }],
      [{ gate: 'MEASURE' }],
    ],
    concepts: ['superposition', 'measurement', 'randomness'],
    explanation:
      'The H gate puts the qubit into superposition: 50% chance of measuring 0 or 1. Run it many times – the outcome is truly random each time!',
  },
  {
    id: 'bell-state',
    name: '🔔 Bell State (Entanglement)',
    description: 'Create a maximally entangled pair of qubits.',
    numQubits: 2,
    initialStates: ['0', '0'],
    grid: [
      [{ gate: 'H' }, null],
      [{ gate: 'CNOT', targetQubit: 1 } as CnotControlCell, { gate: 'CNOT_TARGET', controlQubit: 0 } as CnotTargetCell],
      [{ gate: 'MEASURE' }, { gate: 'MEASURE' }],
    ],
    concepts: ['entanglement', 'superposition'],
    explanation:
      'H puts q0 into superposition, then CNOT entangles q1 with q0. The result is (|00⟩ + |11⟩)/√2 – a Bell state. Measuring always gives 00 or 11, never 01 or 10!',
  },
  {
    id: 'ghz',
    name: '🌟 GHZ State (3-Qubit Entanglement)',
    description: 'Three fully entangled qubits – the GHZ state.',
    numQubits: 3,
    initialStates: ['0', '0', '0'],
    grid: [
      [{ gate: 'H' }, null, null],
      [{ gate: 'CNOT', targetQubit: 1 } as CnotControlCell, { gate: 'CNOT_TARGET', controlQubit: 0 } as CnotTargetCell, null],
      [{ gate: 'CNOT', targetQubit: 2 } as CnotControlCell, null, { gate: 'CNOT_TARGET', controlQubit: 0 } as CnotTargetCell],
      [{ gate: 'MEASURE' }, { gate: 'MEASURE' }, { gate: 'MEASURE' }],
    ],
    concepts: ['entanglement', 'superposition'],
    explanation:
      'The GHZ state (|000⟩ + |111⟩)/√2 entangles all three qubits. Measuring gives 000 or 111 – all three always agree!',
  },
  {
    id: 'phase-kickback',
    name: '🔄 Phase Kickback',
    description: 'See how a controlled gate kicks phase back onto the control qubit.',
    numQubits: 2,
    initialStates: ['0', '1'],
    grid: [
      [{ gate: 'H' }, { gate: 'X' }],
      [{ gate: 'CNOT', targetQubit: 1 } as CnotControlCell, { gate: 'CNOT_TARGET', controlQubit: 0 } as CnotTargetCell],
      [{ gate: 'H' }, null],
      [{ gate: 'MEASURE' }, { gate: 'MEASURE' }],
    ],
    concepts: ['interference', 'measurement'],
    explanation:
      'Start with q1 in |−⟩. CNOT kicks a phase of −1 back to q0. The second H interferes the phases so q0 always measures 1. This "phase kickback" is the heart of many quantum algorithms!',
  },
  {
    id: 'quantum-not',
    name: '🔀 Quantum NOT (X gate)',
    description: 'The X gate flips a qubit from |0⟩ to |1⟩.',
    numQubits: 1,
    initialStates: ['0'],
    grid: [
      [{ gate: 'X' }],
      [{ gate: 'MEASURE' }],
    ],
    concepts: ['measurement'],
    explanation:
      'The X gate is the quantum equivalent of a classical NOT gate. It flips |0⟩ to |1⟩ and |1⟩ to |0⟩. The result is always deterministic!',
  },
  {
    id: 'hzh',
    name: '🌀 H-Z-H = X',
    description: 'Three gates that compose to a bit flip.',
    numQubits: 1,
    initialStates: ['0'],
    grid: [
      [{ gate: 'H' }],
      [{ gate: 'Z' }],
      [{ gate: 'H' }],
      [{ gate: 'MEASURE' }],
    ],
    concepts: ['interference', 'superposition'],
    explanation:
      'H puts the qubit into superposition, Z flips the phase of |1⟩, and the second H interferes the paths. The result is always |1⟩ – the same as an X gate! Interference can implement logic.',
  },
];

// ─── Store ────────────────────────────────────────────────────────────────────

interface CircuitStore {
  numQubits: 1 | 2 | 3;
  initialStates: ('0' | '1')[];
  grid: (CircuitCell | null)[][];   // [step][qubit]
  selectedGate: string | null;      // gate being placed
  cnotPending: { step: number; controlQubit: number } | null; // CNOT placement state
  result: CircuitResult | null;
  currentStep: number;              // for step-through
  activePreset: PresetCircuit | null;

  setNumQubits: (n: 1 | 2 | 3) => void;
  setInitialState: (qubit: number, val: '0' | '1') => void;
  setSelectedGate: (gate: string | null) => void;
  clickCell: (step: number, qubit: number) => void;
  clearCell: (step: number, qubit: number) => void;
  run: () => void;
  stepForward: () => void;
  reset: () => void;
  clearGrid: () => void;
  loadPreset: (id: string) => void;
}

function emptyGrid(): (CircuitCell | null)[][] {
  return Array.from({ length: MAX_STEPS }, () => Array(MAX_QUBITS).fill(null));
}

export const useCircuitStore = create<CircuitStore>((set, get) => ({
  numQubits: 2,
  initialStates: ['0', '0', '0'],
  grid: emptyGrid(),
  selectedGate: null,
  cnotPending: null,
  result: null,
  currentStep: 0,
  activePreset: null,

  setNumQubits: (n) =>
    set((s) => ({
      numQubits: n,
      initialStates: ['0', '0', '0'].slice(0, n) as ('0' | '1')[],
      grid: emptyGrid(),
      result: null,
      currentStep: 0,
      activePreset: null,
    })),

  setInitialState: (qubit, val) =>
    set((s) => {
      const next = [...s.initialStates] as ('0' | '1')[];
      next[qubit] = val;
      return { initialStates: next, result: null };
    }),

  setSelectedGate: (gate) =>
    set({ selectedGate: gate, cnotPending: null }),

  clickCell: (step, qubit) => {
    const { selectedGate, grid, numQubits, cnotPending } = get();
    if (!selectedGate) return;

    const newGrid = grid.map((col) => [...col]);

    if (selectedGate === 'CNOT') {
      if (!cnotPending) {
        // First click: place control dot (pending)
        set({ cnotPending: { step, controlQubit: qubit } });
        return;
      }

      const { step: cStep, controlQubit: cq } = cnotPending;
      // Second click: must be same step, different qubit
      if (step !== cStep || qubit === cq) {
        // Cancel and restart
        set({ cnotPending: { step, controlQubit: qubit } });
        return;
      }
      // Place CNOT: clear existing cells, set control + target
      newGrid[step][cq] = { gate: 'CNOT', targetQubit: qubit } as CnotControlCell;
      newGrid[step][qubit] = { gate: 'CNOT_TARGET', controlQubit: cq } as CnotTargetCell;
      set({ grid: newGrid, cnotPending: null, result: null });
      return;
    }

    // Single-qubit gate: toggle (place or remove)
    const existing = newGrid[step][qubit];
    if (existing?.gate === selectedGate) {
      newGrid[step][qubit] = null;
    } else {
      // Clear any CNOT partner if replacing
      if (existing?.gate === 'CNOT') {
        const tq = (existing as CnotControlCell).targetQubit;
        newGrid[step][tq] = null;
      } else if (existing?.gate === 'CNOT_TARGET') {
        const cq = (existing as CnotTargetCell).controlQubit;
        newGrid[step][cq] = null;
      }
      newGrid[step][qubit] = { gate: selectedGate } as any;
    }
    set({ grid: newGrid, result: null });
  },

  clearCell: (step, qubit) => {
    const { grid } = get();
    const newGrid = grid.map((col) => [...col]);
    const cell = newGrid[step][qubit];
    if (cell?.gate === 'CNOT') {
      newGrid[step][(cell as CnotControlCell).targetQubit] = null;
    } else if (cell?.gate === 'CNOT_TARGET') {
      newGrid[step][(cell as CnotTargetCell).controlQubit] = null;
    }
    newGrid[step][qubit] = null;
    set({ grid: newGrid, result: null });
  },

  run: () => {
    const { numQubits, initialStates, grid } = get();
    const result = runCircuit({
      numQubits,
      initialStates: initialStates.slice(0, numQubits) as ('0' | '1')[],
      grid: grid.map((col) => col.slice(0, numQubits)),
    });
    set({ result, currentStep: result.stepStates.length - 1 });
  },

  stepForward: () => {
    const { numQubits, initialStates, grid, currentStep, result } = get();
    const maxStep = result ? result.stepStates.length - 1 : 0;
    const nextStep = currentStep < maxStep ? currentStep + 1 : 0;

    // Re-run up to nextStep columns
    const colsToRun = result
      ? nextStep  // step 0 = initial, step k = after k columns
      : 1;

    const newResult = runCircuit({
      numQubits,
      initialStates: initialStates.slice(0, numQubits) as ('0' | '1')[],
      grid: grid.map((col) => col.slice(0, numQubits)),
      upToStep: colsToRun,
    });
    set({ result: newResult, currentStep: Math.min(colsToRun, newResult.stepStates.length - 1) });
  },

  reset: () => set({ result: null, currentStep: 0 }),

  clearGrid: () =>
    set({ grid: emptyGrid(), result: null, currentStep: 0, activePreset: null, cnotPending: null }),

  loadPreset: (id) => {
    const preset = PRESET_CIRCUITS.find((p) => p.id === id);
    if (!preset) return;

    // Pad grid columns to numQubits width
    const fullGrid = emptyGrid();
    preset.grid.forEach((col, step) => {
      col.forEach((cell, qubit) => {
        if (step < MAX_STEPS && qubit < MAX_QUBITS) {
          fullGrid[step][qubit] = cell;
        }
      });
    });

    set({
      numQubits: preset.numQubits,
      initialStates: ['0', '0', '0'].map((_, i) =>
        i < preset.initialStates.length ? preset.initialStates[i] : '0'
      ) as ('0' | '1')[],
      grid: fullGrid,
      result: null,
      currentStep: 0,
      activePreset: preset,
      cnotPending: null,
      selectedGate: null,
    });
  },
}));
